import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

interface Particle {
  id: number
  x: number
  y: number
  size: number
  duration: number
  delay: number
}

interface GeometricShape {
  id: number
  type: 'triangle' | 'circle' | 'hexagon' | 'diamond'
  x: number
  y: number
  size: number
  rotation: number
  duration: number
  delay: number
}

export function BackgroundEffects() {
  const [particles, setParticles] = useState<Particle[]>([])
  const [shapes, setShapes] = useState<GeometricShape[]>([])

  useEffect(() => {
    // Generate random particles
    const generatedParticles = Array.from({ length: 5 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 20,
      delay: Math.random() * 5,
    }))
    setParticles(generatedParticles)

    // Generate geometric shapes
    const generatedShapes: GeometricShape[] = [
      { id: 0, type: 'triangle', x: 10, y: 15, size: 60, rotation: 0, duration: 25, delay: 0 },
      { id: 1, type: 'circle', x: 80, y: 30, size: 80, rotation: 0, duration: 30, delay: 5 },
      { id: 2, type: 'hexagon', x: 85, y: 60, size: 60, rotation: 30, duration: 35, delay: 10 },
      { id: 3, type: 'diamond', x: 20, y: 70, size: 50, rotation: 45, duration: 28, delay: 15 },
    ]
    setShapes(generatedShapes)
  }, [])

  const renderShape = (shape: GeometricShape) => {
    const baseClasses = "absolute opacity-20"
    
    switch (shape.type) {
      case 'triangle':
        return (
          <motion.div
            key={shape.id}
            className={baseClasses}
            style={{
              left: `${shape.x}%`,
              top: `${shape.y}%`,
              width: 0,
              height: 0,
              borderLeft: `${shape.size/2}px solid transparent`,
              borderRight: `${shape.size/2}px solid transparent`,
              borderBottom: `${shape.size}px solid rgb(var(--color-4) / 0.2)`,
            }}
            animate={{
              rotate: [0, 360],
              y: [-20, 20, -20],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: shape.duration,
              repeat: Infinity,
              delay: shape.delay,
              ease: "easeInOut",
            }}
          />
        )
      case 'circle':
        return (
          <motion.div
            key={shape.id}
            className={`${baseClasses} rounded-full`}
            style={{
              left: `${shape.x}%`,
              top: `${shape.y}%`,
              width: shape.size,
              height: shape.size,
              background: `radial-gradient(circle, rgb(var(--color-5) / 0.2), rgb(var(--color-5) / 0.05))`,
            }}
            animate={{
              scale: [1, 1.2, 1],
              x: [-10, 10, -10],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: shape.duration,
              repeat: Infinity,
              delay: shape.delay,
              ease: "easeInOut",
            }}
          />
        )
      case 'hexagon':
        return (
          <motion.div
            key={shape.id}
            className={baseClasses}
            style={{
              left: `${shape.x}%`,
              top: `${shape.y}%`,
              width: shape.size,
              height: shape.size * 0.866,
              background: `rgb(var(--color-6) / 0.15)`,
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
            }}
            animate={{
              rotate: [shape.rotation, shape.rotation + 360],
              scale: [1, 0.9, 1],
            }}
            transition={{
              duration: shape.duration,
              repeat: Infinity,
              delay: shape.delay,
              ease: "linear",
            }}
          />
        )
      case 'diamond':
        return (
          <motion.div
            key={shape.id}
            className={baseClasses}
            style={{
              left: `${shape.x}%`,
              top: `${shape.y}%`,
              width: shape.size,
              height: shape.size,
              background: `rgb(var(--color-4) / 0.15)`,
              transform: `rotate(${shape.rotation}deg)`,
            }}
            animate={{
              rotate: [shape.rotation, shape.rotation - 360],
              y: [-15, 15, -15],
              opacity: [0.15, 0.25, 0.15],
            }}
            transition={{
              duration: shape.duration,
              repeat: Infinity,
              delay: shape.delay,
              ease: "easeInOut",
            }}
          />
        )
    }
  }

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-color-1 via-color-2/80 to-color-1" />
      
      {/* Animated gradient overlay */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at top left, rgb(var(--color-4) / 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at top right, rgb(var(--color-6) / 0.1) 0%, transparent 50%),
            radial-gradient(ellipse at bottom center, rgb(var(--color-5) / 0.08) 0%, transparent 70%)
          `,
        }}
        animate={{
          opacity: [0.5, 1, 0.5],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Grid pattern */}
      <motion.div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: `
            linear-gradient(rgb(var(--color-4) / 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgb(var(--color-4) / 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px',
        }}
        animate={{
          x: [0, 100],
          y: [0, 100],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      {/* Geometric shapes */}
      <div className="absolute inset-0">
        {shapes.map(shape => renderShape(shape))}
      </div>

      {/* Floating particles */}
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-gradient-to-r from-color-4 to-color-6 opacity-30"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Glow spots */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full"
        style={{
          background: `radial-gradient(circle, rgb(var(--color-4) / 0.2), transparent 70%)`,
          filter: 'blur(60px)',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      <motion.div
        className="absolute bottom-1/3 right-1/3 w-96 h-96 rounded-full"
        style={{
          background: `radial-gradient(circle, rgb(var(--color-6) / 0.2), transparent 70%)`,
          filter: 'blur(80px)',
        }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      {/* Circuit lines */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        <motion.line
          x1="0"
          y1="30%"
          x2="100%"
          y2="30%"
          stroke="url(#circuit-gradient)"
          strokeWidth="1"
          strokeDasharray="10 20"
          animate={{
            strokeDashoffset: [0, -30],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.line
          x1="20%"
          y1="0"
          x2="20%"
          y2="100%"
          stroke="url(#circuit-gradient)"
          strokeWidth="1"
          strokeDasharray="15 15"
          animate={{
            strokeDashoffset: [0, -30],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.line
          x1="0"
          y1="70%"
          x2="100%"
          y2="70%"
          stroke="url(#circuit-gradient)"
          strokeWidth="1"
          strokeDasharray="5 25"
          animate={{
            strokeDashoffset: [0, -30],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <defs>
          <linearGradient id="circuit-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgb(var(--color-4))" />
            <stop offset="50%" stopColor="rgb(var(--color-5))" />
            <stop offset="100%" stopColor="rgb(var(--color-6))" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  )
}